/**
 * Comparison period utilities, shared across tiles that offer a "Show Comparison
 * Period" toggle (Attribute Sum Line Chart, Resource Utilization Chart).
 *
 * All functions are pure, side-effect-free, and operate on ISO date strings (YYYY-MM-DD).
 * They compute offset date ranges for "Previous Period" and "Year-over-Year" comparisons,
 * optionally offset by more than one period back (periodsBack, 1-4) so a tile can render
 * several previous periods at once.
 */

/**
 * Returns the ISO week number and year for a given date.
 * ISO weeks start on Monday; week 1 contains the first Thursday of the year.
 *
 * @param {Date} date - The date to compute the ISO week for.
 * @returns {{ isoYear: number, isoWeek: number }} ISO year and week number.
 */
function getISOWeekAndYear(date) {
  const d = new Date(
    Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()),
  );
  d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const isoWeek = Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
  return { isoYear: d.getUTCFullYear(), isoWeek };
}

/**
 * Returns the Monday (start) of the given ISO week in the given year.
 *
 * @param {number} isoYear - ISO year.
 * @param {number} isoWeek - ISO week number (1–53).
 * @returns {Date} Date object representing the Monday of that ISO week (UTC).
 */
function mondayOfISOWeek(isoYear, isoWeek) {
  const jan4 = new Date(Date.UTC(isoYear, 0, 4));
  const dayOfWeek = jan4.getUTCDay() || 7;
  const monday = new Date(jan4);
  monday.setUTCDate(jan4.getUTCDate() - dayOfWeek + 1 + (isoWeek - 1) * 7);
  return monday;
}

/**
 * Formats a Date object to an ISO date string (YYYY-MM-DD) using local time components.
 * Avoids UTC conversion issues where timezone offsets shift the date.
 *
 * @param {Date} d - The date to format.
 * @returns {string} ISO date string in local time.
 */
function fmtISO(d) {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/**
 * Formats a Date object to an ISO date string (YYYY-MM-DD) using UTC components.
 * Used for ISO week calculations which operate in UTC.
 *
 * @param {Date} d - The date to format.
 * @returns {string} ISO date string in UTC.
 */
function fmtUTC(d) {
  const year = d.getUTCFullYear();
  const month = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/**
 * Parses an ISO date string into a Date in local time.
 *
 * @param {string} dateStr - ISO date string (YYYY-MM-DD).
 * @returns {Date} Parsed date.
 */
function parseISO(dateStr) {
  return new Date(dateStr + "T00:00:00");
}

/**
 * Constructs a Date for (year, month, day), clamping day down to the last valid
 * day of that month if it overflows (e.g. day=31 in a 30-day month, or Feb 29 in
 * a non-leap year) — the plain Date constructor would otherwise silently roll
 * over into the next month.
 *
 * @param {number} year
 * @param {number} month - 0-indexed (JS Date convention).
 * @param {number} day
 * @returns {Date}
 */
function clampedDate(year, month, day) {
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  return new Date(year, month, Math.min(day, daysInMonth));
}

/**
 * Clamps a period's end date to `todayISO` when the nominal end would
 * otherwise be in the future (e.g. the current year's Dec 31 while today is
 * only in August). Intended to be applied to the CURRENT period's range
 * before computing a comparison range with getComparisonRange/
 * getPreviousPeriodRange/getYoYPeriodRange: those functions anchor each
 * comparison period's end to this range's end (day-of-month / day-of-week /
 * month-day, not a fixed period boundary), so clamping here is what makes a
 * still-in-progress current period compare against the SAME elapsed portion
 * of prior periods, instead of their full (already-complete) length.
 *
 * Day granularity is left untouched — a single day has no "partial period"
 * to clamp.
 *
 * @param {{ start: string, end: string }} range
 * @param {string} granularity - One of "day", "week", "month", "year".
 * @param {string} todayISO - "Today" (e.g. Berlin calendar day) as YYYY-MM-DD.
 * @returns {{ start: string, end: string }}
 */
export function clampRangeEndToToday(range, granularity, todayISO) {
  if (granularity === "day" || !range?.end || !todayISO) return range;
  return range.end > todayISO ? { start: range.start, end: todayISO } : range;
}

/**
 * Filters a chronologically-sorted array of chart buckets (objects with a
 * `bucket_start` ISO date string) down to only those on or before `todayISO`.
 *
 * Used by "limit comparison to current progress" to trim trailing buckets
 * that haven't happened yet from the CURRENT period's own bars/line — not
 * just from comparison series. Since buckets are sorted ascending, this keeps
 * a contiguous leading prefix, so its `.length` is also safe to use as a
 * slice cutoff for other same-length arrays (e.g. per-attribute series that
 * share the same bucket grid).
 *
 * Day granularity is left untouched — a single day has no "partial period"
 * to trim.
 *
 * @param {Array<{ bucket_start: string }>} items
 * @param {string} granularity - One of "day", "week", "month", "year".
 * @param {string} todayISO - "Today" (e.g. Berlin calendar day) as YYYY-MM-DD.
 * @returns {Array} A new array (or the same array if nothing was trimmed).
 */
export function trimFutureBuckets(items, granularity, todayISO) {
  if (!items?.length || granularity === "day" || !todayISO) return items || [];
  return items.filter((d) => d.bucket_start <= todayISO);
}

/**
 * Computes the previous period date range for a given granularity and current range,
 * optionally offset by more than one period back.
 *
 * | Granularity | Comparison Period (periodsBack = 1)        |
 * |-------------|--------------------------------------------|
 * | day         | Previous day                               |
 * | week        | Previous ISO calendar week                 |
 * | month       | Previous calendar month                    |
 * | year        | Previous calendar year                     |
 *
 * @param {string} granularity - One of "day", "week", "month", "year".
 * @param {{ start: string, end: string }} currentRange - Current period start/end as ISO date strings.
 * @param {number} [periodsBack=1] - How many periods back to offset (1-4).
 * @returns {{ start: string, end: string }} Comparison period start/end as ISO date strings.
 */
export function getPreviousPeriodRange(granularity, currentRange, periodsBack = 1) {
  if (!currentRange?.start || !currentRange?.end) return { start: "", end: "" };

  const startDate = parseISO(currentRange.start);
  const endDate = parseISO(currentRange.end);

  if (granularity === "day") {
    const prevStart = new Date(startDate);
    prevStart.setDate(prevStart.getDate() - periodsBack);
    const prevEnd = new Date(endDate);
    prevEnd.setDate(prevEnd.getDate() - periodsBack);
    return { start: fmtISO(prevStart), end: fmtISO(prevEnd) };
  }

  if (granularity === "week") {
    const prevStart = new Date(startDate);
    prevStart.setDate(prevStart.getDate() - 7 * periodsBack);
    const prevEnd = new Date(endDate);
    prevEnd.setDate(prevEnd.getDate() - 7 * periodsBack);
    return { start: fmtISO(prevStart), end: fmtISO(prevEnd) };
  }

  if (granularity === "month") {
    const prevStart = new Date(
      startDate.getFullYear(),
      startDate.getMonth() - periodsBack,
      1,
    );
    // Anchored to the CURRENT period's own end-of-month day (clamped for
    // shorter months), not always the previous month's last day — so a caller
    // that passes a clamped currentRange.end (see clampRangeEndToToday) gets a
    // proportionally-clamped comparison period too, instead of always the full
    // previous month.
    const prevEnd = clampedDate(
      startDate.getFullYear(),
      startDate.getMonth() - periodsBack,
      endDate.getDate(),
    );
    return { start: fmtISO(prevStart), end: fmtISO(prevEnd) };
  }

  if (granularity === "year") {
    const prevYear = startDate.getFullYear() - periodsBack;
    // Same anchoring as the month branch above, via the current period's end
    // month/day rather than always Dec 31.
    const prevEnd = clampedDate(prevYear, endDate.getMonth(), endDate.getDate());
    return { start: `${prevYear}-01-01`, end: fmtISO(prevEnd) };
  }

  return { start: "", end: "" };
}

/**
 * Computes the Year-over-Year comparison date range, optionally offset by more than
 * one year back.
 *
 * | Granularity | Comparison Period (periodsBack = 1)                    |
 * |-------------|--------------------------------------------------------|
 * | day         | Same calendar day, previous year (leap-year safe)      |
 * | week        | Same ISO calendar week, previous year                  |
 * | month       | Same calendar month, previous year                     |
 * | year        | Previous year (identical to Previous Period for year)   |
 *
 * Leap year handling: Feb 29 maps to Feb 28 if the target year is not a leap year.
 *
 * @param {string} granularity - One of "day", "week", "month", "year".
 * @param {{ start: string, end: string }} currentRange - Current period start/end as ISO date strings.
 * @param {number} [periodsBack=1] - How many years back to offset (1-4).
 * @returns {{ start: string, end: string }} Comparison period start/end as ISO date strings.
 */
export function getYoYPeriodRange(granularity, currentRange, periodsBack = 1) {
  if (!currentRange?.start || !currentRange?.end) return { start: "", end: "" };

  const startDate = parseISO(currentRange.start);
  const endDate = parseISO(currentRange.end);

  if (granularity === "year") {
    const prevYear = startDate.getFullYear() - periodsBack;
    // Anchored to the current period's own end month/day (clamped for shorter
    // months / leap years), not always Dec 31 — see the identical pattern in
    // getPreviousPeriodRange's year branch.
    const prevEnd = clampedDate(prevYear, endDate.getMonth(), endDate.getDate());
    return { start: `${prevYear}-01-01`, end: fmtISO(prevEnd) };
  }

  if (granularity === "day") {
    const prevYear = startDate.getFullYear() - periodsBack;
    let month = startDate.getMonth();
    let day = startDate.getDate();
    if (month === 1 && day === 29) {
      const isLeap =
        (prevYear % 4 === 0 && prevYear % 100 !== 0) || prevYear % 400 === 0;
      if (!isLeap) day = 28;
    }
    const prevStart = new Date(prevYear, month, day);
    return { start: fmtISO(prevStart), end: fmtISO(prevStart) };
  }

  if (granularity === "week") {
    const { isoYear, isoWeek } = getISOWeekAndYear(startDate);
    const prevISOYear = isoYear - periodsBack;
    const mon = mondayOfISOWeek(prevISOYear, isoWeek);
    // Anchored to how many days into the CURRENT week currentRange.end reaches
    // (0-6, Monday-based), not always the full Mon-Sun span — so a clamped
    // currentRange.end (partway through the current week) yields the matching
    // partial previous-year week instead of its full 7 days.
    const daysIntoWeek = Math.round((endDate.getTime() - startDate.getTime()) / 86400000);
    const end = new Date(mon);
    end.setUTCDate(mon.getUTCDate() + daysIntoWeek);
    return { start: fmtUTC(mon), end: fmtUTC(end) };
  }

  if (granularity === "month") {
    const prevYear = startDate.getFullYear() - periodsBack;
    const month = startDate.getMonth();
    const prevStart = new Date(prevYear, month, 1);
    // Anchored to the current period's own end-of-month day (clamped), not
    // always the previous year's full month — mirrors getPreviousPeriodRange's
    // month branch.
    const prevEnd = clampedDate(prevYear, month, endDate.getDate());
    return { start: fmtISO(prevStart), end: fmtISO(prevEnd) };
  }

  return { start: "", end: "" };
}

/**
 * The comparison modes, as language-invariant IDs.
 *
 * These are the single source of truth for WHICH modes exist. Deliberately kept
 * separate from the translated option list below: validation and control flow
 * must never depend on display logic. A previous version derived the valid IDs
 * from getComparisonModeOptions(), which broke the moment that function started
 * requiring a `t` — validateComparisonMode called it without one and threw,
 * silently killing the whole Settings "Apply" handler of the Attribute Sum Line
 * Chart. IDs are also persisted in saved tile settings, so they must stay stable.
 */
const COMPARISON_MODE_IDS = ["previousPeriod", "yoy"];

/**
 * Returns the available comparison mode options.
 *
 * Both modes are always offered so the user's selection survives granularity
 * changes. At year granularity the two modes happen to produce the same dates,
 * but they remain distinct selections to preserve user intent.
 *
 * @param {Function} t - i18next t (required — labels are translated).
 * @returns {Array<{ id: string, label: string }>} Available comparison mode options.
 */
export function getComparisonModeOptions(t) {
  return COMPARISON_MODE_IDS.map((id) => ({
    id,
    label: t(`common:comparison.${id}`),
  }));
}

/**
 * Returns the legend label for the comparison line based on the active comparison mode.
 *
 * @param {string} mode - The active comparison mode ("previousPeriod", "yoy", or "previousYear").
 * @returns {string} Human-readable legend label.
 */
export function getComparisonLegendLabel(mode, t) {
  if (mode === "previousPeriod") return t("common:comparison.previousPeriod");
  if (mode === "yoy") return t("common:comparison.yoy");
  if (mode === "previousYear") return t("common:comparison.previousYear");
  return t("common:comparison.generic");
}

/**
 * Computes the comparison period date range for the given mode and granularity,
 * optionally offset by more than one period back.
 *
 * Delegates to either getPreviousPeriodRange or getYoYPeriodRange based on the mode.
 *
 * @param {string} mode - Comparison mode ("previousPeriod", "yoy", or "previousYear").
 * @param {string} granularity - One of "day", "week", "month", "year".
 * @param {{ start: string, end: string }} currentRange - Current period start/end as ISO date strings.
 * @param {number} [periodsBack=1] - How many periods back to offset (1-4).
 * @returns {{ start: string, end: string }} Comparison period start/end as ISO date strings.
 */
export function getComparisonRange(mode, granularity, currentRange, periodsBack = 1) {
  if (mode === "previousPeriod")
    return getPreviousPeriodRange(granularity, currentRange, periodsBack);
  if (mode === "yoy") return getYoYPeriodRange(granularity, currentRange, periodsBack);
  if (mode === "previousYear")
    return getPreviousPeriodRange("year", currentRange, periodsBack);
  return { start: "", end: "" };
}

/**
 * Validates a comparison mode against the current granularity.
 * Returns the mode if valid, or the first available mode as a fallback.
 *
 * @param {string} mode - The currently selected comparison mode.
 * @param {string} granularity - The active granularity.
 * @returns {string} A valid comparison mode for the given granularity.
 */
export function validateComparisonMode(mode) {
  // Validates against IDs only — no translation involved, so this stays callable
  // from anywhere (including non-React code) without a `t`.
  return COMPARISON_MODE_IDS.includes(mode) ? mode : COMPARISON_MODE_IDS[0];
}
